import React, { Fragment } from 'react'

import moment from 'moment'
import numeral from 'numeral'

import { Button } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { array, bool, func, number } from 'prop-types'

import SharedTable from 'Pods/Shared/Table/SharedTable'

import emptyImage from 'images/no_image_thumb_square.png'

import PriceFormatter from 'Utils/PriceFormatter'

import InlineEdit from './Components/InlineEdit'
import PerformanceModal from './Components/PerformanceModal/PerformanceModal'
import PromotionStatusTag from './Components/PromotionStatusTag'
import SubscriptionStatusTag from './Components/SubscriptionStatusTag'
import SuspensionAppeal from './Components/SuspensionAppeal/SuspensionAppeal'
import Verification from './Components/Verification'
import DeleteModelConfirmationModal from './DeleteModelConfirmationModal'
import ModelStatus from './ModelStatus'
import TableActions from './TableActions'
import TotalEarnings from './TotalEarnings'
import getTouchProps from './utils/getTouchProps'
import isMobileDevice from './utils/isMobileDevice'

class Table extends React.Component {
  constructor(props) {
    super(props)

    this.isMobile = null

    this.state = {
      confirmationModalIsOpen: false,
      modelToDelete: null,
      performanceModal: {
        isOpen: false,
        itemId: null,
        itemTitle: null,
        metricType: null,
      },
      table: {
        className: null,
        select: false,
        headers: [],
        rows: [],
        colgroup: [],
      },
    }

    this.closeConfirmationModal = this.closeConfirmationModal.bind(this)
    this.onModelDelete = this.onModelDelete.bind(this)
    this.makeTable = this.makeTable.bind(this)
    this.formatTitle = this.formatTitle.bind(this)
    this.formatThumb = this.formatThumb.bind(this)
    this.renderViewsButton = this.renderViewsButton.bind(this)
    this.openPerformanceModal = this.openPerformanceModal.bind(this)
    this.closePerformanceModal = this.closePerformanceModal.bind(this)
  }

  componentWillReceiveProps(nextProps) {
    this.makeTable(nextProps.data, nextProps.isAddingToCollection)
  }

  componentDidMount() {
    this.isMobile = isMobileDevice()
    this.makeTable(this.props.data, this.props.isAddingToCollection)
  }

  formatTitle(row, column) {
    const titlePrefix = row.type === 'collections' ? 'Pack: ' : ''
    let title

    if (column.status === 'suspended') {
      title = column.title
    } else {
      title = (
        <InlineEdit
          key={`${row.id}-title`}
          attribute="title"
          value={column.title}
          modelId={row.id}
          url={column.itemLink}
          isCollection={row.type === 'collections'}
          isDraft={column.status === 'draft'}
          editCollection={this.props.editCollection}
        />
      )
    }

    return <Fragment><span>{titlePrefix}</span>{title}</Fragment>
  }

  formatThumb(itemLink, modelThumb, row, column) {
    if (row.type === 'collections' && column.status === 'draft') {
      return <div className="collection-draft-trigger" onClick={() => this.props.editCollection(row.id)}>{modelThumb}</div>
    }
    return <a href={itemLink} target="_blank" rel="noreferrer">{modelThumb}</a>
  }

  renderViewsButton(rowId, title, pageviews) {
    const openModal = () => this.openPerformanceModal(rowId, title, 'views')

    return (
      <Button
        type="ghost-primary"
        size="small"
        {...getTouchProps(openModal, this.isMobile)}
        customClass="views-clickable"
      >
        {pageviews}
      </Button>
    )
  }

  makeTable(data, isAddingToCollection) {
    const rows = data.map((row) => {
      const column = row.attributes
      const { modelDownloadData } = column
      const modelStatus = this.props.statuses.filter((status) => status.value === column.status)
      if (!modelStatus.length) {
        window.Sentry.captureMessage(
          'Unexpected model/collection status found',
          {
            extra: {
              id: column.id,
              type: row.type,
              status: column.status,
            },
          }
        )
        return null
      }

      const scoreColor = column.publishingScore >= 6 ? 'text-green' : 'text-warning'
      const modelImage = column.primaryImage.thumbUrl
      const modelThumb = <img src={modelImage && modelImage.length > 0 ? modelImage : emptyImage} alt={column.title} />

      return {
        id: row.id,
        columns: [{
          content: this.formatThumb(column.itemLink, modelThumb, row, column),
          customClass: 'product-inline-image u-text-center no-pad no-border',
        }, {
          content: this.formatTitle(row, column),
          customClass: 'product-text__title product-inline-edit no-border',
        }, ...(this.props.cgtStandardEnabled ? [{
          content: <Verification column={column} />,
          customClass: 'u-text-center',
        }] : []), {
          content: moment(column.publishedAt).format('YYYY.MM.DD'),
          customClass: 'product-text__date u-text-center',
        }, {
          content: moment(column.updatedAt).format('YYYY.MM.DD'),
          customClass: 'product-text__date u-text-center',
        }, {
          content: (column.status == 'suspended') ? (column.price) : (<InlineEdit key={`${row.id}-price`} attribute="price" value={column.price} modelId={row.id} isPrintable={column.printReady} />),
          customClass: 'product-text__price product-inline-edit text-semibold u-text-center',
        }, {
          content: numeral(column.publishingScore).format('0.0'),
          customClass: `product-text__score text-semibold u-text-center ${scoreColor}`,
        }, {
          content: this.renderViewsButton(row.id, column.title, column.pageviews),
          customClass: 'product-text__views u-text-center',
        }, ...(this.props.isSalesBoostEnabled ? [{
          content: (
            <PromotionStatusTag
              promotionStatus={column.promotionStatus}
            />
          ),
          customClass: 'u-text-center',
        }] : []), {
          content: (
            <TotalEarnings
              id={row.id}
              salesCount={column.salesCount}
              earnings={PriceFormatter(column.revenue)}
              onClick={() => this.openPerformanceModal(row.id, column.title, 'earnings')}
            />
          ),
          customClass: 'product-text__revenue u-text-center',
        }, {
          content: (
            <>
              <ModelStatus status={modelStatus[0].label} icon={modelStatus[0].icon} statusType={column.approveStatus} id={row.id} expiresAt={column.expiresAt} />
              {this.props.sellerSuspensionAppealsEnabled && column.status === 'suspended' && (
                <SuspensionAppeal
                  itemId={row.id}
                  suspensionReason={column.suspensionReason}
                  approveStatus={column.approveStatus}
                  appealStatus={column.appealStatus}
                />
              )}
            </>
          ),
          customClass: `${modelStatus[0].class} text-semibold product-text__status`,
        }, ...(this.props.isSubscriptionColumnEnabled ? [{
          content: (
            <SubscriptionStatusTag
              itemId={row.id}
              subscriptionSubscribed={column.subscriptionSubscribed}
              subscriptionTierBand={column.subscriptionTierBand}
              plansRestructureEnabled={this.props.plansRestructureEnabled}
            />
          ),
          customClass: 'u-text-center',
        }] : []), {
          content: <TableActions
            model={row}
            status={column.status}
            onModelDelete={this.onModelDelete}
            allowToDeleteModels={this.props.allowToDeleteModels}
            editCollection={this.props.editCollection}
            isAddingToCollection={isAddingToCollection}
            downloadData={modelDownloadData}
            isDownloadMyModelsEnabled={this.props.isDownloadMyModelsEnabled}
            isSalesBoostEnabled={this.props.isSalesBoostEnabled}
            salesBoostPoints={this.props.salesBoostPoints}
            limitTagCampaignsAndCostInflation={this.props.limitTagCampaignsAndCostInflation}
            onCampaignSubmitted={this.props.onCampaignSubmitted}
          />,
          customClass: 'product-actions',
        }],
      }
    }).filter(Boolean)

    this.setState({
      table: {
        className: 'table-with-borders products-listing',
        select: true,
        headers: false,
        rows,
        colgroup: this.props.colgroup,
        disabledSelect: !!isAddingToCollection,
      },
    })
  }

  onModelDelete(model) {
    this.setState({
      confirmationModalIsOpen: true,
      modelToDelete: model,
    })
  }

  closeConfirmationModal() {
    this.setState({
      confirmationModalIsOpen: false,
      modelToDelete: null,
    })
  }

  openPerformanceModal(itemId, itemTitle, metricType) {
    this.setState({
      performanceModal: {
        isOpen: true,
        itemId,
        itemTitle,
        metricType,
      },
    })
  }

  closePerformanceModal() {
    this.setState({
      performanceModal: {
        isOpen: false,
        itemId: null,
        itemTitle: null,
        metricType: null,
      },
    })
  }

  render() {
    const { performanceModal } = this.state

    const { tableMinWidth } = this.props
    const tableStyle = tableMinWidth ? { minWidth: tableMinWidth, tableLayout: 'fixed' } : undefined

    return (
      <div>
        {this.state.modelToDelete
          && (
            <DeleteModelConfirmationModal
              modalIsOpen={this.state.confirmationModalIsOpen}
              modelName={this.state.modelToDelete.attributes.title}
              modelId={this.state.modelToDelete.id}
              closeModal={this.closeConfirmationModal}
            />
          )}
        {performanceModal.isOpen && (
          <PerformanceModal
            itemId={performanceModal.itemId}
            itemTitle={performanceModal.itemTitle}
            metricType={performanceModal.metricType}
            onClose={this.closePerformanceModal}
          />
        )}
        <SharedTable
          table={{ ...this.state.table, tableStyle }}
          onCheckboxClick={this.props.onCheckboxClick}
          selectedRows={this.props.selectedRows}
        />
      </div>
    )
  }
}

Table.propTypes = {
  data: array.isRequired,
  statuses: array.isRequired,
  onCheckboxClick: func.isRequired,
  selectedRows: array.isRequired,
  allowToDeleteModels: bool,
  editCollection: func.isRequired,
  isAddingToCollection: bool.isRequired,
  isDownloadMyModelsEnabled: bool,
  isSalesBoostEnabled: bool,
  isSubscriptionColumnEnabled: bool,
  plansRestructureEnabled: bool,
  colgroup: array,
  limitTagCampaignsAndCostInflation: bool,
  tableMinWidth: number,
  onCampaignSubmitted: func.isRequired,
  sellerSuspensionAppealsEnabled: bool,
}

Table.defaultProps = {
  allowToDeleteModels: true,
  isDownloadMyModelsEnabled: false,
  isSalesBoostEnabled: false,
  isSubscriptionColumnEnabled: false,
  plansRestructureEnabled: false,
  colgroup: [],
  limitTagCampaignsAndCostInflation: false,
  tableMinWidth: null,
  sellerSuspensionAppealsEnabled: false,
}

export default Table
